/**
 * Custom Analysis Type Icons
 *
 * React components for custom SVG analysis icons.
 * These match the HeroIcon signature (ComponentType<SVGProps<SVGSVGElement>>).
 */

import type { SVGProps } from "react";

/**
 * Vision System Icon - Camera/eye with corner brackets
 * Used for Image Analysis type
 */
export function VisionSystemIcon(props: Readonly<SVGProps<SVGSVGElement>>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <g>
        <path d="M17.7,12s-2.4,3.2-5.9,3.2-5.9-3.2-5.9-3.2c0,0,2.4-3.2,5.9-3.2s5.9,3.2,5.9,3.2Z" />
        <circle cx="11.8" cy="12" r="2.8" />
        <circle cx="11.8" cy="12" r=".4" />
      </g>
      <polyline points="3.5 9.6 3.5 6.5 7.5 6.5" />
      <polyline points="20.5 9.6 20.5 6.5 16.5 6.5" />
      <polyline points="20.5 14.4 20.5 17.5 16.5 17.5" />
      <polyline points="3.5 14.4 3.5 17.5 7.5 17.5" />
    </svg>
  );
}

/**
 * Sieving Icon - Stacked sieves with particles
 * Used for Gradation/Sieve Analysis type
 */
export function SievingIcon(props: Readonly<SVGProps<SVGSVGElement>>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <ellipse cx="12" cy="4.8" rx="8.6" ry="2" />
      <path d="M20.6,4.8v4.3c0,1.1-3.8,2-8.6,2s-8.6-.9-8.6-2v-4.3" />
      <path d="M3.4,11.7c0-.2.2-.3.4-.2,1.1.8,4.3,1.4,8.2,1.4s7.1-.6,8.2-1.4.4,0,.4.2v3.6c0,1.1-3.8,2-8.6,2s-8.6-.9-8.6-2v-3.6Z" />
      <line x1="6.5" y1="6.3" x2="9.7" y2="3.1" />
      <line x1="9.7" y1="6.3" x2="12.9" y2="3.1" />
      <line x1="12.6" y1="6.7" x2="16.1" y2="3.2" />
      <line x1="16.2" y1="6.3" x2="18.7" y2="3.8" />
      <line x1="4.1" y1="5.4" x2="6.1" y2="3.4" />
      <line x1="17.5" y1="6.3" x2="14.3" y2="3.1" />
      <line x1="14.3" y1="6.3" x2="11.1" y2="3.1" />
      <line x1="11.4" y1="6.7" x2="7.9" y2="3.2" />
      <line x1="7.8" y1="6.3" x2="5.3" y2="3.8" />
      <line x1="19.9" y1="5.4" x2="17.9" y2="3.4" />
      <rect
        x="5.4"
        y="19.4"
        width="1.4"
        height="1.4"
        transform="translate(16 1.6) rotate(45)"
      />
      <rect
        x="9.4"
        y="19.4"
        width="1.4"
        height="1.4"
        transform="translate(9.2 -2.4) rotate(24.4)"
      />
      <rect
        x="13.3"
        y="19.4"
        width="1.4"
        height="1.4"
        transform="translate(-10.1 15.8) rotate(-45)"
      />
      <rect x="17.3" y="19.4" width="1.4" height="1.4" />
      <line x1="17.5" y1="6.6" x2="17.5" y2="10.2" />
      <line x1="17.5" y1="12.6" x2="17.5" y2="16.2" />
      <line x1="19" y1="6.3" x2="19" y2="9.9" />
      <line x1="19" y1="12.3" x2="19" y2="16" />
    </svg>
  );
}

/**
 * Wash Loss Icon - Aggregate washing through sieve with fine particles
 * Used for Wash Loss/ASTM C117 Analysis type
 * Represents water washing aggregate through a No. 200 sieve
 */
export function WashLossIcon(props: Readonly<SVGProps<SVGSVGElement>>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      {/* Sieve pan container */}
      <ellipse cx="12" cy="9" rx="8.5" ry="2" />
      <path d="M3.5,9v3c0,1.1,3.8,2,8.5,2s8.5-.9,8.5-2v-3" />
      {/* Mesh pattern in sieve */}
      <line x1="6" y1="9" x2="6" y2="12" />
      <line x1="9" y1="9" x2="9" y2="12" />
      <line x1="12" y1="9" x2="12" y2="12" />
      <line x1="15" y1="9" x2="15" y2="12" />
      <line x1="18" y1="9" x2="18" y2="12" />
      {/* Water streams from above */}
      <path d="M8,2.5c0,0,0,2.5,0,4" />
      <path d="M12,2c0,0,0,2.5,0,4.5" />
      <path d="M16,2.5c0,0,0,2.5,0,4" />
      {/* Fine particles/water passing through below */}
      <circle cx="8" cy="17" r=".6" />
      <circle cx="12" cy="16" r=".5" />
      <circle cx="16" cy="17.5" r=".6" />
      <circle cx="10" cy="19" r=".5" />
      <circle cx="14" cy="19.5" r=".5" />
      {/* Small droplet trails */}
      <line x1="9" y1="14.5" x2="9" y2="15.5" />
      <line x1="12" y1="14" x2="12" y2="15" />
      <line x1="15" y1="14.5" x2="15" y2="15.5" />
    </svg>
  );
}
